import { ROUTE_BOOK_LOCATION } from './constants';

/**
 * Search indexing for a tenant on a marketing-site theme preset (Aperio).
 *
 * The preset's marketing site (aperiomobility.com) is what should rank. Of this
 * portal, the one page worth a search result is the booking landing,
 * `/book/location` - "book Aperio" should land there. Everything after it
 * (services, add-ons, schedule, confirmation, pay link), and every account,
 * login, invoice, franchise and fleet screen, is either a step of a funnel
 * that means nothing out of context or a private page behind a login, so it
 * carries `noindex, follow`: not listed, but its links still count.
 *
 * The landing gets a self-referencing canonical with no query string. The
 * marketing site hands visitors over as `/book/location?street=…&postal=…`,
 * so without it every address anyone ever typed could become its own URL in
 * the index. Its links to the marketing site are the preset's header and
 * footer (AperioSiteHeader / MarketingFooter).
 *
 * Google and Bing both render JavaScript and honour a robots meta tag the page
 * sets itself. public/index.html sets the same tags synchronously on the
 * preset's own hosts, before the app loads; this keeps them right as the
 * visitor (or the renderer) moves between routes. A tenant without a preset -
 * Go Oil and every other - never reaches any of this: nothing is added to its
 * head and robots.txt is untouched.
 *
 * Keep the path list in step with the inline script in public/index.html.
 */
export const PRESET_INDEXABLE_PATHS: readonly string[] = [ROUTE_BOOK_LOCATION];

export const ROBOTS_INDEX = 'index, follow';
export const ROBOTS_NOINDEX = 'noindex, follow';

/** `/book/location/` and `/book/location` are the same route. */
function normalizePath(pathname: string): string {
  const p = (pathname || '/').split(/[?#]/)[0];
  return p.length > 1 ? p.replace(/\/+$/, '') : p;
}

export function isPresetIndexablePath(pathname: string): boolean {
  return PRESET_INDEXABLE_PATHS.includes(normalizePath(pathname));
}

/** The robots directive a preset route carries. */
export function presetRobotsFor(pathname: string): string {
  return isPresetIndexablePath(pathname) ? ROBOTS_INDEX : ROBOTS_NOINDEX;
}

/** Canonical URL of an indexable route (no query or hash); else null. */
export function presetCanonicalFor(
  pathname: string,
  origin: string,
): string | null {
  if (!isPresetIndexablePath(pathname)) return null;
  return `${origin.replace(/\/$/, '')}${normalizePath(pathname)}`;
}

/**
 * Writes `<meta name="robots">` and `<link rel="canonical">` for `pathname`.
 * A route that is not indexable has no canonical: pointing a noindexed page
 * at itself would send the two opposite signals.
 */
export function applyPresetRobots(
  pathname: string,
  origin: string = window.location.origin,
): void {
  const { head } = document;
  let robots = head.querySelector<HTMLMetaElement>('meta[name="robots"]');
  if (!robots) {
    robots = document.createElement('meta');
    robots.setAttribute('name', 'robots');
    head.appendChild(robots);
  }
  robots.setAttribute('content', presetRobotsFor(pathname));

  const canonical = presetCanonicalFor(pathname, origin);
  let link = head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (canonical) {
    if (!link) {
      link = document.createElement('link');
      link.rel = 'canonical';
      head.appendChild(link);
    }
    link.href = canonical;
  } else if (link) {
    link.remove();
  }
}
